import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, NetworkError } from '@/lib/api'
import { createDictationSession, getSpeechRecognition, type DictationStatus } from '@/lib/dictation'
import { canRecord, createRecording } from '@/lib/recorder'
import { useSession } from '@/lib/session'
import { voiceApi } from '@/lib/voiceApi'
import { getVoiceLocale, getWhisperLanguage, readVoiceEngine, type VoiceEngine } from '@/lib/voicePreferences'
import { useOptionalWorkspace } from '@/lib/workspace'

/**
 * `starting`, `listening`, `stopping` come from browser dictation;
 * `recording` and `transcribing` from the Whisper path, where the clip is
 * captured first and the words arrive after upload.
 */
export type VoiceStatus = DictationStatus | 'recording' | 'transcribing'

/**
 * One microphone for a form with several dictation targets.
 *
 * Two engines behind one control. When the server runs the Whisper engine and
 * the person has not chosen otherwise, a click records (up to 90 s), a second
 * click stops, the clip is transcribed on the server and appended to the
 * field. Otherwise the browser's own speech recognition streams words in as
 * they are spoken. Either way only text reaches the form.
 */
export function useVoiceInput(enabled: boolean) {
  const { user, voiceTranscription } = useSession()
  const workspace = useOptionalWorkspace()
  const [status, setStatus] = useState<VoiceStatus>('idle')
  const [error, setError] = useState<string | null>(null)
  const [field, setField] = useState<string | null>(null)
  const [seconds, setSeconds] = useState(0)
  const dictation = useRef<ReturnType<typeof createDictationSession> | null>(null)
  const recording = useRef<ReturnType<typeof createRecording> | null>(null)
  const upload = useRef<AbortController | null>(null)

  const preferred: VoiceEngine = readVoiceEngine(user?.id)
  const whisperAvailable = voiceTranscription && Boolean(workspace?.slug) && canRecord()
  const engine: VoiceEngine = preferred === 'whisper' && whisperAvailable ? 'whisper' : 'browser'
  const supported = engine === 'whisper' ? true : Boolean(getSpeechRecognition())

  const cancel = useCallback(() => {
    dictation.current?.cancel()
    dictation.current = null
    recording.current?.cancel()
    recording.current = null
    upload.current?.abort()
    upload.current = null
    setStatus('idle')
    setSeconds(0)
  }, [])

  const reset = useCallback(() => {
    cancel()
    setField(null)
    setError(null)
  }, [cancel])

  useEffect(() => {
    setStatus('idle')
    setError(null)
    setField(null)
    if (!enabled) return
    const onHidden = () => { if (document.hidden) cancel() }
    document.addEventListener('visibilitychange', onHidden)
    window.addEventListener('pagehide', cancel)
    return () => {
      dictation.current?.dispose()
      dictation.current = null
      recording.current?.dispose()
      recording.current = null
      upload.current?.abort()
      upload.current = null
      document.removeEventListener('visibilitychange', onHidden)
      window.removeEventListener('pagehide', cancel)
    }
  }, [enabled, cancel])

  const startBrowser = (initialText: string, onText: (text: string) => void) => {
    const Recognition = getSpeechRecognition()
    if (!Recognition) {
      setError('Voice input is unavailable in this browser. Try Chrome or Safari, or continue typing.')
      return
    }
    try {
      dictation.current = createDictationSession(Recognition, {
        language: getVoiceLocale(user?.id), initialText, onText, onStatus: setStatus, onError: setError,
      })
      dictation.current.start()
    } catch {
      setStatus('idle')
      setError('Voice input could not start in this browser. You can still write your task.')
    }
  }

  const startWhisper = (initialText: string, onText: (text: string) => void) => {
    const slug = workspace?.slug
    if (!slug) return
    const prefix = initialText.trimEnd()
    const session = createRecording({
      onStatus: (state) => {
        if (recording.current !== session) return
        if (state === 'starting') setStatus('starting')
        else if (state === 'recording') setStatus('recording')
        else if (state === 'processing') setStatus('transcribing')
        else if (state === 'cancelled' || state === 'failed') {
          recording.current = null
          setStatus('idle')
          setSeconds(0)
        }
      },
      onTick: (value) => { if (recording.current === session) setSeconds(value) },
      onError: (message) => { if (recording.current === session) setError(message) },
      onWav: (wav) => {
        if (recording.current !== session) return
        recording.current = null
        setStatus('transcribing')
        const controller = new AbortController()
        upload.current = controller
        void voiceApi.transcribe(slug, wav, getWhisperLanguage(user?.id), controller.signal)
          .then(({ text }) => {
            if (upload.current !== controller) return
            onText(prefix ? `${prefix} ${text}` : text)
          })
          .catch((cause: unknown) => {
            if (upload.current !== controller) return
            setError(transcriptionError(cause))
          })
          .finally(() => {
            if (upload.current !== controller) return
            upload.current = null
            setStatus('idle')
            setSeconds(0)
          })
      },
    })
    recording.current = session
    void session.start()
  }

  const start = (target: string, initialText: string, onText: (text: string) => void) => {
    if (!enabled || status !== 'idle') return
    dictation.current?.dispose()
    dictation.current = null
    setError(null)
    setField(target)
    setSeconds(0)
    if (engine === 'whisper') startWhisper(initialText, onText)
    else startBrowser(initialText, onText)
  }

  const stop = () => {
    dictation.current?.stop()
    recording.current?.stop()
  }

  return {
    status, error, supported, field, engine, seconds,
    busy: status !== 'idle',
    start, stop, cancel, reset,
  }
}

function transcriptionError(cause: unknown): string {
  if (cause instanceof ApiError) {
    if (cause.code === 'no_speech') return 'No speech was detected. Try again and speak closer to your microphone.'
    if (cause.code === 'voice_unavailable') return 'Server transcription is not available right now. Choose the browser engine in Settings → Voice, or type instead.'
    if (cause.code === 'voice_busy' || cause.isRateLimited) return 'Transcription is busy right now. Wait a moment and try again. Your text is kept.'
    if (cause.code === 'voice_timeout') return 'Transcription took too long. Try a shorter recording.'
    return cause.message || 'Transcription failed. Your text is kept; please try again.'
  }
  if (cause instanceof NetworkError) return 'Transcription could not reach the server. Check your connection and try again. Your text is kept.'
  if (cause instanceof DOMException && cause.name === 'AbortError') return ''
  return 'Transcription failed. Your text is kept; please try again.'
}
